'use strict';

/**
 * Images candidates : toutes celles rendues en `loading="lazy"` par Fusion
 * (`JF.Neos.Fusion:Prototype.ResponsiveImageTag` applique l'attribut par défaut).
 */
const LAZY_IMAGE_SELECTOR = 'img[loading="lazy"]';

/**
 * Marge verticale (en fraction de la hauteur viewport) ajoutée sous la ligne de flottaison
 * pour considérer une image comme « quasi-visible » au chargement. Évite de pénaliser
 * les images situées juste en dessous du premier écran (barre d'URL mobile, reflow, etc.).
 */
const VIEWPORT_BOTTOM_MARGIN_RATIO = 0.1;

/**
 * L'image retenue comme candidat LCP doit représenter une surface significative :
 * on exige une largeur ET une hauteur minimales pour éviter de promouvoir une icône.
 */
const LCP_MIN_WIDTH_PX = 200;
const LCP_MIN_HEIGHT_PX = 200;

/**
 * Vrai si l'élément est réellement visible (pas `display:none`, pas `visibility:hidden`,
 * pas à l'intérieur d'un conteneur caché type onglet inactif / slide non active).
 *
 * @param {Element} el
 * @returns {boolean}
 */
function isElementVisible(el) {
    if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ checkVisibilityCSS: true, checkOpacity: false });
    }
    return el.getClientRects().length > 0;
}

/**
 * Vrai si le rectangle occupe une surface non nulle (layout déjà calculé).
 *
 * @param {DOMRect} rect
 * @returns {boolean}
 */
function hasLayout(rect) {
    return rect.width > 0 && rect.height > 0;
}

/**
 * Vrai si une partie de l'élément chevauche le viewport élargi d'une petite marge basse.
 *
 * @param {DOMRect} rect
 * @param {number} viewportHeight
 * @returns {boolean}
 */
function intersectsInitialViewport(rect, viewportHeight) {
    const bottomLimit = viewportHeight + viewportHeight * VIEWPORT_BOTTOM_MARGIN_RATIO;
    return rect.top < bottomLimit && rect.bottom > 0;
}

/**
 * Optimisation des images au-dessus de la ligne de flottaison.
 *
 * Stratégie :
 * - Fusion rend toutes les images de contenu avec `loading="lazy"` + `decoding="async"` par défaut
 *   (voir `JF.Neos.Fusion:Prototype.ResponsiveImageTag`), car le contenu est éditorial et
 *   le template ne peut pas deviner quelles images apparaîtront dans le premier écran.
 * - Ce script parcourt les images lazy et retire l'attribut `loading` sur celles visibles
 *   au chargement, pour que le navigateur les télécharge immédiatement.
 * - La plus grande image visible est désignée comme candidat LCP : on ajoute
 *   `fetchpriority="high"` et on retire `decoding="async"` pour laisser le navigateur
 *   arbitrer le décodage (valeur `auto`), ce qui favorise le rendu initial.
 *
 * Doit s'exécuter tôt (au plus tard à `DOMContentLoaded`, avant la peinture initiale),
 * pour que le preload scanner déclenche le téléchargement dès que possible.
 */
export function promoteAboveTheFoldImages() {
    const images = document.querySelectorAll(LAZY_IMAGE_SELECTOR);
    if (!images.length) {
        return;
    }

    const viewportHeight = window.innerHeight;
    let lcpCandidate = null;
    let lcpSize = 0;

    images.forEach((img) => {
        if (!isElementVisible(img)) {
            return;
        }

        const rect = img.getBoundingClientRect();
        if (!hasLayout(rect)) {
            return;
        }

        if (!intersectsInitialViewport(rect, viewportHeight)) {
            return;
        }

        img.removeAttribute('loading');

        if (rect.width < LCP_MIN_WIDTH_PX || rect.height < LCP_MIN_HEIGHT_PX) {
            return;
        }

        const size = rect.width * rect.height;
        if (size > lcpSize) {
            lcpSize = size;
            lcpCandidate = img;
        }
    });

    if (lcpCandidate) {
        lcpCandidate.setAttribute('fetchpriority', 'high');
        lcpCandidate.removeAttribute('decoding');
    }
}
